import { supabase } from '../client'
import type { CloudRewardUsage } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── REWARD USAGES (Cloud) ─────────────────────────────────────────── */

// Recency of reward picks — one row per (user, reward). See ADR 0003 and the
// reward_usages migration for why local_id is the catalogue reward id.

export async function getCloudRewardUsages(userId: string, since?: Date): Promise<CloudRewardUsage[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('reward_usages')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('used_at', { ascending: false })
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get reward usages error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get reward usages error', error)
    return []
  }
}

export async function getCloudRewardUsageIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('reward_usages')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)
    if (error) {
      logSupabaseError('Get reward usage IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get reward usage IDs error', error)
    return []
  }
}

export async function getCloudRewardUsagesByIds(userId: string, ids: string[]): Promise<CloudRewardUsage[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('reward_usages')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get reward usages by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get reward usages by IDs error', error)
    return []
  }
}

export async function getDeletedCloudRewardUsages(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('reward_usages')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted reward usages error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted reward usages error', error)
    return []
  }
}

export async function createCloudRewardUsage(
  userId: string,
  usage: Omit<CloudRewardUsage, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudRewardUsage | null> {
  if (!supabase) return null
  try {
    // UPSERT on (user_id, reward_id): the table holds one row per reward, and a
    // second device may have inserted it first. A plain insert would hit the
    // unique constraint and lose the pick.
    const { data, error } = await supabase
      .from('reward_usages')
      .upsert({ ...usage, user_id: userId }, { onConflict: 'user_id,reward_id' })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create reward usage error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create reward usage error', error)
    return null
  }
}

export async function updateCloudRewardUsage(
  usageId: string,
  updates: Partial<Omit<CloudRewardUsage, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('reward_usages')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', usageId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update reward usage error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update reward usage error', error)
    return null
  }
}

export async function deleteCloudRewardUsage(usageId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('reward_usages')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', usageId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete reward usage error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete reward usage error', error)
    return false
  }
}
